/**
 * Explore Events / LiveEvents table: load and mutate user_events via explore API.
 */
import { apiEndpoints } from '@config/env';
import { postData, deleteData } from '@utils/global';
import {
  fetchExploreTagRows,
  exploreRowKind,
  type ExploreTagMergedRow,
} from '@services/userEventsExploreService';
import { getCurrentDatasetTimezone } from '@store/datasetTimezoneStore';

/** Matches MapContainer explore COMMENT/NOTE padding (server uses 5s for imports). */
export const EXPLORE_NOTE_FOCUS_PADDING_MS = 5000;

export interface ExploreEventsTableRow {
  kind: 'user_event' | 'dataset_event';
  user_event_id: number | null;
  event_id: number | null;
  event_type: string;
  comment: string;
  user_name?: string | null;
  focus_time: string | null;
  start_time: string | null;
  end_time: string | null;
  /** Primary display/sort instant (focus_time, else start_time). */
  datetime: string;
}

export function exploreEventsTableRowKey(row: ExploreEventsTableRow): string {
  if (row.user_event_id != null) {return `ue:${row.user_event_id}`;}
  if (row.event_id != null) {return `de:${row.event_id}`;}
  return `${row.datetime}|${row.comment}|${row.user_name ?? ''}|${row.event_type}`;
}

export function exploreEventsTableDisplayTime(row: ExploreEventsTableRow): string {
  return row.focus_time || row.start_time || row.datetime;
}

function mergedRowToTableRow(row: ExploreTagMergedRow): ExploreEventsTableRow | null {
  const kind = exploreRowKind(row);
  if (!kind) {return null;}
  const focus = row.focus_time?.trim() || null;
  const start = row.start_time?.trim() || null;
  const dt = focus || start;
  if (!dt) {return null;}
  const comment = (row.comment ?? '').trim();
  if (!comment) {return null;}
  return {
    kind,
    user_event_id: row.user_event_id,
    event_id: row.event_id,
    event_type: String(row.event_type ?? ''),
    comment: row.comment ?? '',
    user_name: row.user_name,
    focus_time: focus,
    start_time: start,
    end_time: row.end_time?.trim() || null,
    datetime: dt,
  };
}

export async function fetchExploreEventsTable(args: {
  className: string;
  projectId: number;
  datasetId: number;
  signal?: AbortSignal;
}): Promise<ExploreEventsTableRow[]> {
  const timezone = getCurrentDatasetTimezone() || 'UTC';
  const merged = await fetchExploreTagRows({
    className: args.className,
    projectId: args.projectId,
    datasetId: args.datasetId,
    timezone,
    signal: args.signal,
  });
  const out: ExploreEventsTableRow[] = [];
  for (const row of merged) {
    const mapped = mergedRowToTableRow(row);
    if (mapped) {out.push(mapped);}
  }
  return out;
}

function utcYmdFromIso(iso: string): string | undefined {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {return undefined;}
  return d.toISOString().slice(0, 10);
}

export async function createExploreNoteUserEvent(args: {
  projectId: number;
  focusIso: string;
  comment: string;
}): Promise<boolean> {
  const focusMs = new Date(args.focusIso).getTime();
  if (Number.isNaN(focusMs)) {return false;}
  const focusIso = new Date(focusMs).toISOString();
  const ymd = utcYmdFromIso(focusIso);
  const body: Record<string, unknown> = {
    project_id: args.projectId,
    event_type: 'NOTE',
    tags: { Comment: args.comment },
    focus_time: focusIso,
    start_time: new Date(focusMs - EXPLORE_NOTE_FOCUS_PADDING_MS).toISOString(),
    end_time: new Date(focusMs + EXPLORE_NOTE_FOCUS_PADDING_MS).toISOString(),
  };
  if (ymd) {body.date = ymd;}
  const res = await postData(apiEndpoints.app.userEvents, body);
  return res.success === true;
}

export async function deleteExploreUserEvent(args: {
  projectId: number;
  userEventId: number;
  signal?: AbortSignal;
}): Promise<boolean> {
  const url = `${apiEndpoints.app.userEvents}/${args.userEventId}?project_id=${encodeURIComponent(String(args.projectId))}`;
  const res = await deleteData(url, undefined, args.signal);
  return res.success === true;
}

export function canDeleteExploreEventsTableRow(row: ExploreEventsTableRow): boolean {
  return (
    row.kind === 'user_event' &&
    row.user_event_id != null &&
    Number.isFinite(row.user_event_id) &&
    row.user_event_id > 0 &&
    String(row.event_type || '').toUpperCase() === 'NOTE'
  );
}
